import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { publicSitePath } from "@/utils/publicSite";
import { clearPublicSiteAuthHint, setPublicSiteAuthHint } from "@/utils/publicSite";
import { hasPaidFeatureAccess, hasProAccess, paidFeatureMessage, proFeatureMessage } from "@/utils/planMessaging";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import Dashboard from "@/pages/Dashboard";
import RentalAnalysis from "@/pages/RentalAnalysis";
import FlipCalculator from "@/pages/FlipCalculator";
import AirbnbCalculator from "@/pages/AirbnbCalculator";
import MortgageCalculator from "@/pages/MortgageCalculator";
import ReportPage from "@/pages/ReportPage";
import BRRRRCalculator from "@/pages/BRRRRCalculator";
import ExpenseTemplates from "@/pages/ExpenseTemplates";
import RentEstimator from "@/pages/RentEstimator";
import PortfolioTracker from "@/pages/PortfolioTracker";
import DealComparison from "@/pages/DealComparison";
import Exchange1031 from "@/pages/Exchange1031";
import SellerFinancing from "@/pages/SellerFinancing";
import DebtPaydown from "@/pages/DebtPaydown";
import Onboarding from "@/pages/Onboarding";
import DealManager from "@/pages/DealManager";
import DealDetail from "@/pages/DealDetail";
import ReportVault from "@/pages/ReportVault";
import PropertyResearch from "@/pages/PropertyResearch";
import PerformanceTracker from "@/pages/PerformanceTracker";
import PartnershipManager from "@/pages/PartnershipManager";
import TaxStrategy from "@/pages/TaxStrategy";
import RenovationEstimator from "@/pages/RenovationEstimator";
import UpgradePage from "@/pages/UpgradePage";
import BillingCheckoutPage from "@/pages/BillingCheckoutPage";
import AuthCallback from "@/pages/AuthCallback";
import BrandingSettings from "@/pages/BrandingSettings";
import CompleteRegistrationPage from "@/pages/CompleteRegistrationPage";
import QuickScreen from "@/pages/QuickScreen";
import AdminDashboard from "@/pages/AdminDashboard";
import CheckoutPage from "@/pages/CheckoutPage";
import AIChatWidget from "@/components/AIChatWidget";
import NotFoundPage from "@/pages/NotFoundPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
const ACCESS_TOKEN_REFRESH_WINDOW_MS = 5 * 60 * 1000;
const REFRESH_CHECK_THROTTLE_MS = 60 * 1000;
const ACTIVITY_WINDOW_MS = 15 * 60 * 1000;
const SESSION_ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

const getTokenExpiryMs = (token) => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(window.atob(padded));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (_error) {
    return null;
  }
};

const shouldClearAuthAfterRefreshFailure = (error) => {
  const statusCode = error?.response?.status;
  return statusCode === 401 || statusCode === 403;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const tokenRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const tokenExpiryMsRef = useRef(null);
  const lastRefreshCheckAtRef = useRef(0);
  const lastActivityAtRef = useRef(Date.now());

  const clearAuthState = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    tokenExpiryMsRef.current = null;
    clearPublicSiteAuthHint();
  }, []);

  const setAuthenticatedSession = useCallback((accessToken, userData) => {
    localStorage.removeItem("token");
    setToken(accessToken);
    setUser(userData);
    tokenExpiryMsRef.current = getTokenExpiryMs(accessToken);
    lastActivityAtRef.current = Date.now();
    setPublicSiteAuthHint();
  }, []);

  const refreshSession = useCallback(async () => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = axios
        .post(`${API}/auth/refresh`, {}, { withCredentials: true })
        .then((response) => {
          const { access_token, user: userData } = response.data;
          setAuthenticatedSession(access_token, userData);
          return access_token;
        })
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }

    return refreshPromiseRef.current;
  }, [setAuthenticatedSession]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const maybeRefreshNearExpiry = useCallback(async () => {
    if (!tokenRef.current) return null;

    const now = Date.now();
    if (now - lastRefreshCheckAtRef.current < REFRESH_CHECK_THROTTLE_MS) {
      return null;
    }
    lastRefreshCheckAtRef.current = now;

    const expiryMs = tokenExpiryMsRef.current ?? getTokenExpiryMs(tokenRef.current);
    if (!expiryMs) return null;
    tokenExpiryMsRef.current = expiryMs;

    const msUntilExpiry = expiryMs - now;
    if (msUntilExpiry <= 0 || msUntilExpiry <= ACCESS_TOKEN_REFRESH_WINDOW_MS) {
      return refreshSession();
    }

    return null;
  }, [refreshSession]);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        await refreshSession();
      } catch (error) {
        if (shouldClearAuthAfterRefreshFailure(error)) {
          clearAuthState();
        }
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, [clearAuthState, refreshSession]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const isAuthRefresh = originalRequest.url?.includes("/auth/refresh");
        if (error.response?.status !== 401 || originalRequest._retry || isAuthRefresh) {
          throw error;
        }

        originalRequest._retry = true;
        try {
          const freshToken = await refreshSession();
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${freshToken}`,
          };
          return axios(originalRequest);
        } catch (refreshError) {
          if (shouldClearAuthAfterRefreshFailure(refreshError)) {
            clearAuthState();
          }
          throw error;
        }
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [clearAuthState, refreshSession]);

  useEffect(() => {
    if (!token || !user) return undefined;

    const onActivity = () => {
      lastActivityAtRef.current = Date.now();
      void maybeRefreshNearExpiry();
    };

    SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onActivity();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(() => {
      if (Date.now() - lastActivityAtRef.current <= ACTIVITY_WINDOW_MS) {
        void maybeRefreshNearExpiry();
      }
    }, 60 * 1000);

    return () => {
      SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [token, user, maybeRefreshNearExpiry]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    setAuthenticatedSession(access_token, userData);
    return userData;
  };

  const loginWithToken = (accessToken, userData) => {
    setAuthenticatedSession(accessToken, userData);
  };

  // Registration only stages the signup — the account is created by verifyEmail.
  const register = async (name, email, password, country = "CA", selectedPlan = "free") => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      country,
      selected_plan: selectedPlan
    }, { withCredentials: true });
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-email`, { email, code }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    setAuthenticatedSession(access_token, userData);
    return userData;
  };

  const resendVerification = async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, { email }, { withCredentials: true });
    return response.data;
  };

  const logout = async () => {
    setLoggingOut(true);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Local cleanup still happens even if the network request fails.
    } finally {
      clearAuthState();
    }
  };

  const refreshUser = async (accessTokenOverride = null) => {
    const activeToken = accessTokenOverride || tokenRef.current;
    if (!activeToken) return null;
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${activeToken}` }
    });
    setUser(response.data);
    return response.data;
  };

  const updateTier = async (tier) => {
    const response = await axios.put(
      `${API}/auth/tier`,
      { tier },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(response.data);
    return response.data;
  };

  const updatePreferences = async (preferences) => {
    const response = await axios.put(
      `${API}/auth/preferences`,
      preferences,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Update local user state with new preferences
    setUser(prev => ({
      ...prev,
      preferences: { ...prev?.preferences, ...response.data.preferences }
    }));
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, loggingOut, login, loginWithToken, register, verifyEmail, resendVerification, logout, updateTier, updatePreferences, refreshUser, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, loggingOut } = useAuth();
  
  if (loading || loggingOut) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PaidFeatureRoute = ({ featureName, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user && !hasPaidFeatureAccess(user?.tier)) {
      toast.error(paidFeatureMessage(featureName), { id: `paid-feature-${featureName}` });
    }
  }, [featureName, loading, user]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hasPaidFeatureAccess(user?.tier)) {
    return <Navigate to="/upgrade" replace state={{ blockedFeature: featureName }} />;
  }
  
  return children;
};

const ProFeatureRoute = ({ featureName, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user && !hasProAccess(user?.tier)) {
      toast.error(proFeatureMessage(featureName), { id: `pro-feature-${featureName}` });
    }
  }, [featureName, loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hasProAccess(user?.tier)) {
    return <Navigate to="/upgrade" replace state={{ blockedFeature: featureName }} />;
  }

  return children;
};

const PUBLIC_SITE_ROUTE_OVERRIDES = {
  "/rental-calculator": "/calculator",
  "/brrrr": "/calculator",
  "/flip": "/calculator",
  "/airbnb": "/calculator",
  "/stress-testing": "/calculator"
};

const PublicSiteRedirect = ({ to }) => {
  const location = useLocation();
  const targetPath = to || PUBLIC_SITE_ROUTE_OVERRIDES[location.pathname] || location.pathname;
  const targetUrl = publicSitePath(`${targetPath}${location.search}${location.hash}`);

  useEffect(() => {
    window.location.replace(targetUrl);
  }, [targetUrl]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center text-slate-300">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-amber-500 mx-auto mb-4"></div>
        <p>Redirecting to Flowguard...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-slate-900">
        <Toaster position="top-right" richColors closeButton offset={56} />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicSiteRedirect to="/" />} />
            <Route path="/pricing" element={<PublicSiteRedirect />} />
            <Route path="/calculator" element={<PublicSiteRedirect />} />
            <Route path="/blog" element={<PublicSiteRedirect />} />
            <Route path="/blog/:slug" element={<PublicSiteRedirect />} />
            <Route path="/rental-calculator" element={<PublicSiteRedirect />} />
            <Route path="/brrrr" element={<PublicSiteRedirect />} />
            <Route path="/flip" element={<PublicSiteRedirect />} />
            <Route path="/airbnb" element={<PublicSiteRedirect />} />
            <Route path="/stress-testing" element={<PublicSiteRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/complete" element={<CompleteRegistrationPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/quick-screen" element={<ProtectedRoute><QuickScreen /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
            <Route path="/billing/checkout" element={<ProtectedRoute><BillingCheckoutPage /></ProtectedRoute>} />
            <Route path="/deals" element={<ProtectedRoute><DealManager /></ProtectedRoute>} />
            <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportVault /></ProtectedRoute>} />
            <Route path="/property-research" element={<ProFeatureRoute featureName="Property Research"><PropertyResearch /></ProFeatureRoute>} />
            <Route path="/deals/:dealId/performance" element={<ProtectedRoute><PerformanceTracker /></ProtectedRoute>} />
            <Route path="/partnerships" element={<ProFeatureRoute featureName="Partnership Mode"><PartnershipManager /></ProFeatureRoute>} />
            <Route path="/tax-strategy" element={<ProFeatureRoute featureName="Tax Strategy"><TaxStrategy /></ProFeatureRoute>} />
            <Route path="/renovation-estimator" element={<ProFeatureRoute featureName="Renovation Estimator"><RenovationEstimator /></ProFeatureRoute>} />
            <Route path="/rental-analysis" element={<ProtectedRoute><RentalAnalysis /></ProtectedRoute>} />
            <Route path="/rental-analysis/:id" element={<ProtectedRoute><RentalAnalysis /></ProtectedRoute>} />
            <Route path="/flip-calculator" element={<ProtectedRoute><FlipCalculator /></ProtectedRoute>} />
            <Route path="/airbnb-calculator" element={<ProFeatureRoute featureName="Airbnb Calculator"><AirbnbCalculator /></ProFeatureRoute>} />
            <Route path="/mortgage-calculator" element={<ProtectedRoute><MortgageCalculator /></ProtectedRoute>} />
            <Route path="/brrrr-calculator" element={<ProtectedRoute><BRRRRCalculator /></ProtectedRoute>} />
            <Route path="/brrrr-calculator/:id" element={<ProtectedRoute><BRRRRCalculator /></ProtectedRoute>} />
            <Route path="/expense-templates" element={<PaidFeatureRoute featureName="Expense Templates"><ExpenseTemplates /></PaidFeatureRoute>} />
            <Route path="/rent-estimator" element={<ProtectedRoute><RentEstimator /></ProtectedRoute>} />
            <Route path="/portfolio" element={<ProFeatureRoute featureName="Portfolio Tracker"><PortfolioTracker /></ProFeatureRoute>} />
            <Route path="/compare" element={<ProFeatureRoute featureName="Deal Comparison"><DealComparison /></ProFeatureRoute>} />
            <Route path="/1031-exchange" element={<ProFeatureRoute featureName="1031 Exchange"><Exchange1031 /></ProFeatureRoute>} />
            <Route path="/seller-financing" element={<ProFeatureRoute featureName="Seller Financing"><SellerFinancing /></ProFeatureRoute>} />
            <Route path="/debt-paydown" element={<ProFeatureRoute featureName="Debt Paydown"><DebtPaydown /></ProFeatureRoute>} />
            <Route path="/settings" element={<ProtectedRoute><BrandingSettings /></ProtectedRoute>} />
            <Route path="/branding" element={<Navigate to="/settings" replace />} />
            <Route path="/report/:type/:id" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <AIChatWidget />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
